import { Button, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Tabs, TabsList, TabsTrigger } from '@1trading/react';
import { Pencil, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { useInstruments, usePerpOrderLimitsList, usePositionLimitsList } from '@/hooks/queries/markets/use-markets';
import { getErrorMessage } from '@/lib/api/error';
import {
  findDriftedMarketIds,
  indexPerpOrderLimitsBySymbol,
  indexPositionLimitsBySymbol,
  isFuturesMarket,
  MARKET_STATE_OPTIONS,
  type Market,
  type MarketStateLabel,
  type MarketType,
  type PerpOrderLimit,
  type PositionLimit,
  toDisplayState,
  toWireState,
  updateMarket,
  updateMarketsBulk,
} from '@/services/api/markets';
import { BulkMarketLimitsDialog } from './bulk-market-limits-dialog';
import { MarketLimitsDialog } from './market-limits-dialog';
import { MarketsTable } from './markets-table';

// Five tabs share ONE table component, differing only in the filtered rows
const MARKET_TABS: ReadonlyArray<{ key: string; label: string; type: MarketType | null }> = [
  { key: 'all', label: 'All Markets', type: null },
  { key: 'spot', label: 'Spot Markets', type: 'SPOT' },
  { key: 'perp', label: 'Perp Markets', type: 'PERP' },
  { key: 'equity_future', label: 'Equity Future Markets', type: 'EQUITY_FUTURE' },
  { key: 'dated_future', label: 'Dated Future Markets', type: 'DATED_FUTURE' },
];

const DEFAULT_TAB_KEY = 'all';

// Stable references — inline fallbacks would recreate per render
const EMPTY_MARKETS: Market[] = [];
const EMPTY_LIMITS: PerpOrderLimit[] = [];
const EMPTY_POSITION_LIMITS: PositionLimit[] = [];
const EMPTY_SELECTION: ReadonlySet<string> = new Set();

const PROPAGATION_NOTE = 'PLEASE NOTE: This may take up to 30 seconds to update.';

const pluralise = (count: number) => (count === 1 ? 'market' : 'markets');

export const MarketsPanel = () => {
  const bulkSelectId = useId();
  const instrumentsQuery = useInstruments();
  const limitsQuery = usePerpOrderLimitsList();
  const positionLimitsQuery = usePositionLimitsList();
  const [activeTabKey, setActiveTabKey] = useState<string>(DEFAULT_TAB_KEY);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(EMPTY_SELECTION);
  const [bulkStateLabel, setBulkStateLabel] = useState<MarketStateLabel | ''>('');
  const [limitsMarket, setLimitsMarket] = useState<Market | null>(null);
  const [isBulkLimitsOpen, setIsBulkLimitsOpen] = useState(false);

  const markets = instrumentsQuery.data ?? EMPTY_MARKETS;
  const activeTab = MARKET_TABS.find((tab) => tab.key === activeTabKey) ?? MARKET_TABS[0];

  const visibleMarkets = useMemo(() => (activeTab.type === null ? markets : markets.filter((market) => market.type === activeTab.type)), [markets, activeTab.type]);
  const perpOrderLimitsBySymbol = useMemo(() => indexPerpOrderLimitsBySymbol(limitsQuery.data ?? EMPTY_LIMITS), [limitsQuery.data]);
  const positionLimitsBySymbol = useMemo(() => indexPositionLimitsBySymbol(positionLimitsQuery.data ?? EMPTY_POSITION_LIMITS), [positionLimitsQuery.data]);
  // Across ALL markets, not the active tab, so drift on another tab is not missed
  const driftedMarketIds = useMemo(
    () => findDriftedMarketIds(markets, perpOrderLimitsBySymbol, positionLimitsBySymbol),
    [markets, perpOrderLimitsBySymbol, positionLimitsBySymbol],
  );

  // The limit columns are secondary to the table — a failure leaves them blank
  // rather than hiding the whole markets list behind an error banner.
  useEffect(() => {
    if (limitsQuery.isError) toast.error(`Failed to load order limits: ${getErrorMessage(limitsQuery.error)}`);
  }, [limitsQuery.isError, limitsQuery.error]);

  useEffect(() => {
    if (positionLimitsQuery.isError) toast.error(`Failed to load position limits: ${getErrorMessage(positionLimitsQuery.error)}`);
  }, [positionLimitsQuery.isError, positionLimitsQuery.error]);

  const selectedMarkets = useMemo(() => visibleMarkets.filter((market) => selectedIds.has(market.id)), [visibleMarkets, selectedIds]);
  const selectedFuturesMarkets = useMemo(() => selectedMarkets.filter(isFuturesMarket), [selectedMarkets]);

  // Selection covers the active tab's visible rows only; after any refetch or tab
  // switch, prune selected ids that no longer exist in the list (legacy
  // did this with a watcher).
  useEffect(() => {
    setSelectedIds((previous) => {
      const visibleIds = new Set(visibleMarkets.map((market) => market.id));
      const next = new Set([...previous].filter((id) => visibleIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [visibleMarkets]);

  const handleToggleRow = (id: string, isSelected: boolean) =>
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (isSelected) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleToggleAll = (isSelected: boolean) => setSelectedIds(isSelected ? new Set(visibleMarkets.map((market) => market.id)) : EMPTY_SELECTION);

  const handleClearSelection = () => {
    setSelectedIds(EMPTY_SELECTION);
    setBulkStateLabel('');
  };

  // Single state change: the select never applies immediately — it opens
  // the shared audit dialog first.
  const handleStateChange = (market: Market, label: MarketStateLabel) => {
    const nextState = toWireState(label);
    // Deliberate fix: suppress the confirm dialog when the chosen state
    // equals the current one (legacy re-triggered a pointless update for POST_ONLY).
    if (nextState === market.state) return;
    void confirmAction({
      title: 'Confirm Market State Change',
      description: (
        <>
          Are you sure you want to change the state of {market.id} from {toDisplayState(market.state)} to {label}?{' '}
          <span className="font-bold text-red-500">{PROPAGATION_NOTE}</span>
        </>
      ),
      actionLabel: 'Update State',
      onConfirm: async (comment) => {
        try {
          // Slim payload only — bop-api strips everything but { id, state } anyway
          await updateMarket({ id: market.id, state: nextState, comment });
          toast.success(`Successfully updated ${market.id} state to ${label}. Note: propagation may take up to 30 seconds.`);
        } catch {
          // Failure closes the dialog; the select reverts automatically because its
          // value derives from query data — no optimistic selection kept
          toast.error('Error updating market state');
        }
        // The refetch may still show the OLD state for ~30s — the toasts warn about it
        await instrumentsQuery.refetch();
      },
    });
  };

  // Bulk state change
  const handleBulkUpdate = () => {
    if (bulkStateLabel === '' || selectedIds.size === 0) return;
    const label = bulkStateLabel;
    const ids = [...selectedIds];
    void confirmAction({
      title: 'Confirm Bulk Market State Change',
      description: (
        <>
          Are you sure you want to change the state of the following {ids.length} {pluralise(ids.length)} to {label}? <span className="font-bold">{ids.join(', ')}</span>{' '}
          <span className="font-bold text-red-500">{PROPAGATION_NOTE}</span>
        </>
      ),
      actionLabel: 'Update States',
      onConfirm: async (comment) => {
        try {
          const result = await updateMarketsBulk({ ids, state: toWireState(label), comment });
          // Partial success is HTTP 200 — always inspect failed[]
          if (result.failed.length > 0) {
            toast.error(`Updated ${result.updated.length} market(s) to ${label}, but failed for: ${result.failed.map((failure) => failure.id).join(', ')}`);
          } else {
            toast.success(`Successfully updated ${result.updated.length} market(s) to ${label}. Note: propagation may take up to 30 seconds.`);
          }
        } catch {
          toast.error('Error updating market states');
        }
        // Always: clear selection, refetch instruments
        handleClearSelection();
        await instrumentsQuery.refetch();
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Tabs onValueChange={setActiveTabKey} value={activeTabKey}>
        <TabsList>
          {MARKET_TABS.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {driftedMarketIds.length > 0 ? (
        <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm" role="status">
          <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-amber-500" />
          <div>
            <p className="font-medium text-amber-600 dark:text-amber-500">
              Limits out of sync for {driftedMarketIds.length} {pluralise(driftedMarketIds.length)}: {driftedMarketIds.join(', ')}
            </p>
            <p className="text-muted-foreground">
              The public API publishes different limits from what the exchange enforces. The flagged cells show which: open the limits for each market and save to resync.
            </p>
          </div>
        </div>
      ) : null}

      {selectedIds.size > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-4 py-3">
          <span className="text-sm font-medium">
            {selectedIds.size} {pluralise(selectedIds.size)} selected
          </span>
          <div className="flex items-center gap-2">
            <Label className="sr-only" htmlFor={bulkSelectId}>
              New state
            </Label>
            <Select onValueChange={(value) => setBulkStateLabel(value as MarketStateLabel)} value={bulkStateLabel || undefined}>
              <SelectTrigger className="w-40" id={bulkSelectId}>
                <SelectValue placeholder="New state" />
              </SelectTrigger>
              <SelectContent>
                {MARKET_STATE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button disabled={bulkStateLabel === ''} onClick={handleBulkUpdate} type="button" variant="primary">
            Update Selected
          </Button>
          <Button className="gap-2" disabled={selectedFuturesMarkets.length === 0} onClick={() => setIsBulkLimitsOpen(true)} type="button" variant="outline">
            <Pencil className="size-4" />
            Edit Limits{selectedFuturesMarkets.length > 0 ? ` (${selectedFuturesMarkets.length})` : ''}
          </Button>
          <Button onClick={handleClearSelection} type="button" variant="outline">
            Clear
          </Button>
        </div>
      ) : null}

      {instrumentsQuery.isError ? (
        // Deliberate fix: legacy rendered an empty table on fetch
        // failure, indistinguishable from "no markets" — surface the error + Retry.
        <QueryErrorBanner message={`Failed to fetch markets: ${getErrorMessage(instrumentsQuery.error)}`} onRetry={() => instrumentsQuery.refetch()} />
      ) : (
        // Table stays mounted through refetches; skeletons only on the initial load
        <MarketsTable
          isLimitsLoading={limitsQuery.isLoading}
          isLoading={instrumentsQuery.isLoading}
          isPositionLimitsLoading={positionLimitsQuery.isLoading}
          markets={visibleMarkets}
          onEditLimits={setLimitsMarket}
          onStateChange={handleStateChange}
          onToggleAll={handleToggleAll}
          onToggleRow={handleToggleRow}
          perpOrderLimitsBySymbol={perpOrderLimitsBySymbol}
          positionLimitsBySymbol={positionLimitsBySymbol}
          selectedIds={selectedIds}
        />
      )}

      {isBulkLimitsOpen ? (
        <BulkMarketLimitsDialog markets={selectedFuturesMarkets} onClose={() => setIsBulkLimitsOpen(false)} skippedCount={selectedMarkets.length - selectedFuturesMarkets.length} />
      ) : null}
      {limitsMarket ? <MarketLimitsDialog market={limitsMarket} onClose={() => setLimitsMarket(null)} /> : null}
    </div>
  );
};
MarketsPanel.displayName = 'MarketsPanel';
